/**
 * Center-crop an image file to a square (cover) before avatar upload.
 * Ensures downstream consumers that render circular avatars without object-fit: cover
 * still receive a square source image.
 */
export async function cropImageFileToSquare(file: File, outputSize = 1000): Promise<File> {
    if (!file.type.startsWith('image/')) {
        return file;
    }

    const objectUrl = URL.createObjectURL(file);

    try {
        const image = await loadImage(objectUrl);
        const cropSize = Math.min(image.naturalWidth, image.naturalHeight);
        const sourceX = (image.naturalWidth - cropSize) / 2;
        const sourceY = (image.naturalHeight - cropSize) / 2;

        const canvas = document.createElement('canvas');
        canvas.width = outputSize;
        canvas.height = outputSize;

        const context = canvas.getContext('2d');
        if (!context) {
            throw new Error('Canvas is not available');
        }

        context.drawImage(image, sourceX, sourceY, cropSize, cropSize, 0, 0, outputSize, outputSize);

        const blob = await canvasToBlob(canvas, 'image/jpeg', 0.92);
        if (!blob) {
            throw new Error('Failed to crop image');
        }

        return new File([blob], file.name, { type: blob.type || 'image/jpeg' });
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Failed to load image'));
        image.src = src;
    });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
    return new Promise(resolve => {
        canvas.toBlob(resolve, type, quality);
    });
}
